import {Button, IconButton, Tooltip} from 'platform/components';
import {ThemeIconKey} from 'platform/foundation';

import {getActionButtonSize} from '../../../../../../../../utils/getActionButtonSize';
import {type RowActionItem} from '../../../../../../../../utils/partitionRowActions';

type ActionButtonSize = ReturnType<typeof getActionButtonSize>;

type RowActionButtonProps = {
  action: RowActionItem;
  buttonSize: ActionButtonSize;
  isDisabled: boolean;
  onClick: (actionKey: string, event: React.MouseEvent) => void;
};

/**
 * Renders a single primary row action as a tooltip-wrapped control. Icon-backed
 * actions render as an IconButton; actions without an icon fall back to a labeled
 * ghost <Button> so the title stays a visible affordance (an empty IconButton would
 * render an invisible square).
 */
export function RowActionButton(props: RowActionButtonProps) {
  const {action} = props;
  const iconValue = action.definition.icon.value as ThemeIconKey | undefined;

  const control = iconValue ? (
    <IconButton
      size={props.buttonSize}
      icon={iconValue}
      aria-label={action.definition.title ?? 'row action'}
      onClick={(event: React.MouseEvent) => props.onClick(action.actionKey, event)}
      isDisabled={props.isDisabled}
      data-testid={`button-rowAction.${action.actionKey}`}
    />
  ) : (
    <Button
      size="small"
      variant="ghost"
      title={action.definition.title}
      aria-label={action.definition.title ?? 'row action'}
      onClick={(event) => props.onClick(action.actionKey, event as React.MouseEvent)}
      isDisabled={props.isDisabled}
      data-testid={`button-rowAction.${action.actionKey}`}
    />
  );

  return (
    <Tooltip
      label={action.definition.title}
      description={action.definition.description}
      placement="left"
      hasAutoWidth
    >
      {control}
    </Tooltip>
  );
}
